import { Fragment, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { extractErr } from '../../api/error-message';

// Wired to GET /admin/communication-logs (+ /summary). Every SMS, email and push
// the platform attempts — OTPs, order updates, broadcasts, admin test sends —
// with the provider's answer. SKIPPED rows are kept on purpose: "the gateway
// was off" or "the template is not approved" is usually the answer support is
// looking for. OTP codes are masked server-side. Rows older than 90 days are
// deleted automatically.

const PER_PAGE = 25;

interface CommRow {
  id: string;
  channel: 'SMS' | 'EMAIL' | 'PUSH';
  purpose: 'OTP' | 'NOTIFICATION' | 'BROADCAST' | 'TEST';
  status: 'SENT' | 'FAILED' | 'SKIPPED';
  templateKey: string | null;
  eventType: string | null;
  recipientActorType: string | null;
  recipientActorId: string | null;
  recipientName: string | null;
  recipient: string | null;
  subject: string | null;
  body: string | null;
  provider: string | null;
  providerRef: string | null;
  reason: string | null;
  errorDetail: string | null;
  notificationId: string | null;
  createdAt: string;
}

interface Summary {
  sinceHours: number;
  sent: number;
  failed: number;
  skipped: number;
  byChannel: Record<string, Record<string, number>>;
}

const CHANNEL_TABS = ['ALL', 'SMS', 'EMAIL', 'PUSH'] as const;
type ChannelKey = (typeof CHANNEL_TABS)[number];
const STATUSES = ['ALL', 'SENT', 'FAILED', 'SKIPPED'] as const;
const PURPOSES = ['ALL', 'OTP', 'NOTIFICATION', 'BROADCAST', 'TEST'] as const;

const ACTOR_LABEL: Record<string, string> = {
  CUSTOMER: 'Customer',
  SELLER: 'Seller',
  DELIVERY_PARTNER: 'Partner',
  ADMIN: 'Admin',
};

// Plain-language versions of the adapter's reason codes.
const REASON_LABEL: Record<string, string> = {
  provider_disabled: 'Gateway not configured / switched off',
  missing_credentials: 'Gateway API key missing',
  no_recipient: 'No phone / email on file',
  template_inactive: 'Template not approved or switched off',
  missing_variable: 'A template value was empty',
  missing_template_id: 'No MSG91 template ID',
  send_failed: 'Provider rejected the message',
  not_configured: 'Push (FCM) not configured',
  auth_failed: 'FCM authentication failed',
};

function statusStyle(status: string): string {
  if (status === 'SENT') return 'badge-success';
  if (status === 'FAILED') return 'badge-danger';
  return '';
}

/** Display-only: `SKIPPED` → `Skipped`. */
const sentence = (s: string) => s[0] + s.slice(1).toLowerCase();

export function CommunicationLogsPage() {
  const [rows, setRows] = useState<CommRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [channel, setChannel] = useState<ChannelKey>('ALL');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('ALL');
  const [purpose, setPurpose] = useState<(typeof PURPOSES)[number]>('ALL');
  const [search, setSearch] = useState('');
  // Server-searched (the log is paged by the API) across every column below:
  // date, status, channel, type, recipient name / number / account, message
  // text and the "Why" reason.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: PER_PAGE };
      if (channel !== 'ALL') params.channel = channel;
      if (status !== 'ALL') params.status = status;
      if (purpose !== 'ALL') params.purpose = purpose;
      if (debouncedSearch) params.q = debouncedSearch;
      const [list, sum] = await Promise.all([
        api.get<{ data: CommRow[]; pagination: { total: number } }>('/admin/communication-logs', { params }),
        api.get<Summary>('/admin/communication-logs/summary', { params: { sinceHours: 24 } }),
      ]);
      if (requestId !== latestRequest.current) return;
      setRows(list.data.data);
      setTotal(list.data.pagination.total);
      setSummary(sum.data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, channel, status, purpose, debouncedSearch]);
  // Back to page 1 whenever a filter changes, so a filter on page 4 does not
  // silently show nothing.
  useEffect(() => { setPage(1); }, [channel, status, purpose, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const searching = debouncedSearch.length > 0;
  const smsFailed = summary?.byChannel.SMS?.FAILED ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Communication log"
        description="Every SMS, email and push sent to customers, sellers and partners, with the gateway's answer. Kept for 90 days."
      />

      {summary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <SummaryTile label="Sent (24h)" value={summary.sent} />
          <SummaryTile label="Failed (24h)" value={summary.failed} tone={summary.failed > 0 ? 'danger' : undefined} />
          <SummaryTile label="SMS failed (24h)" value={smsFailed} tone={smsFailed > 0 ? 'danger' : undefined} />
          <SummaryTile label="Skipped (24h)" value={summary.skipped} tone={summary.skipped > 0 ? 'warning' : undefined} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {CHANNEL_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setChannel(t)}
            aria-pressed={channel === t}
            className="fchip"
          >
            {t === 'ALL' ? 'All' : t === 'EMAIL' ? 'Email' : t === 'PUSH' ? 'Push' : 'SMS'}
          </button>
        ))}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
          className="input input-sm w-auto"
          aria-label="Status"
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'Any status' : s[0] + s.slice(1).toLowerCase()}</option>)}
        </select>
        <select
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as (typeof PURPOSES)[number])}
          className="input input-sm w-auto"
          aria-label="Type"
        >
          {PURPOSES.map((p) => <option key={p} value={p}>{p === 'ALL' ? 'Any type' : p[0] + p.slice(1).toLowerCase()}</option>)}
        </select>
      </div>

      <TableSearch
        value={search}
        onChange={setSearch}
        placeholder="Search by recipient, phone, message, status, channel, reason or date"
        resultCount={!loading && searching ? total : undefined}
      />

      <div className="panel overflow-hidden">
        {loading ? (
          <SkeletonRows cols={6} />
        ) : rows.length === 0 && searching ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for “${debouncedSearch}”`}
            body="Try another word, or clear the search to see every message in this view."
            action={<button type="button" onClick={() => setSearch('')} className="btn btn-secondary">Clear search</button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="forum"
            title="No messages in this view"
            body="SMS, email and push attempts appear here as the platform sends them. Try a different channel or filter."
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Channel</th>
                <th>To</th>
                <th>Message</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className={`cursor-pointer ${expanded === r.id ? 'is-selected' : ''}`}
                  >
                    <td className="whitespace-nowrap text-[13.5px] text-ink-500">
                      {new Date(r.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' })}
                    </td>
                    <td>
                      <span className={`badge ${statusStyle(r.status)}`}>
                        {sentence(r.status)}
                      </span>
                    </td>
                    <td className="text-ink-700">
                      {r.channel === 'SMS' ? 'SMS' : sentence(r.channel)}
                      <span className="block text-[12.5px] text-ink-500">{r.purpose === 'OTP' ? 'OTP' : r.purpose.toLowerCase()}</span>
                    </td>
                    <td className="text-ink-700">
                      {r.recipientName ?? r.recipient ?? '—'}
                      <span className="block text-[12.5px] text-ink-500">
                        {r.recipientName && r.recipient ? `${r.recipient} · ` : ''}
                        {r.recipientActorType ? ACTOR_LABEL[r.recipientActorType] ?? r.recipientActorType : ''}
                      </span>
                    </td>
                    <td className="max-w-[320px] text-ink-700">
                      <span className="font-semibold text-ink-900">{r.templateKey ?? r.subject ?? r.eventType ?? '—'}</span>
                      <span className="block truncate text-[13.5px] text-ink-500">{r.channel === 'EMAIL' ? r.subject : r.body}</span>
                    </td>
                    <td className="max-w-[220px] text-[13.5px] text-ink-500">
                      {r.reason ? (REASON_LABEL[r.reason] ?? r.reason) : r.status === 'SENT' ? '—' : ''}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-surface-sunk hover:bg-surface-sunk">
                      <td colSpan={6} className="!py-4">
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px] md:grid-cols-4">
                          <Detail label="Provider" value={r.provider} />
                          <Detail label="Provider ref" value={r.providerRef} mono />
                          <Detail label="Event" value={r.eventType} mono />
                          <Detail label="Template" value={r.templateKey} mono />
                          <Detail label="Recipient" value={r.recipient} mono />
                          <Detail label="Account" value={r.recipientActorId ? `${ACTOR_LABEL[r.recipientActorType ?? ''] ?? r.recipientActorType} #${r.recipientActorId}` : null} />
                          <Detail label="Notification" value={r.notificationId ? `#${r.notificationId}` : null} />
                          <Detail label="Subject" value={r.subject} />
                        </dl>
                        {r.errorDetail && (
                          <p className={`mt-3 whitespace-pre-wrap rounded-md px-3.5 py-2.5 text-[13.5px] ${r.status === 'FAILED' ? 'bg-danger-light text-danger-ink' : 'bg-surface-2 text-ink-700'}`}>
                            {r.errorDetail}
                          </p>
                        )}
                        {r.body && (
                          <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-ink-900 p-3.5 font-mono text-[12.5px] text-ink-100">
                            {r.body}
                          </pre>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!loading && (
          <Pagination page={page} totalPages={totalPages} totalItems={total} perPage={PER_PAGE} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone?: 'danger' | 'warning' }) {
  const toneClass = tone === 'danger' ? 'text-danger-ink' : tone === 'warning' ? 'text-warning-ink' : 'text-ink-900';
  return (
    <div className="panel p-5">
      <p className="text-[13.5px] font-semibold text-ink-500">{label}</p>
      <p className={`mt-1.5 font-tabular text-kpi ${toneClass}`}>{value}</p>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[12.5px] font-semibold text-ink-500">{label}</dt>
      <dd className={`mt-0.5 break-all text-ink-900 ${mono ? 'sku' : ''}`}>{value}</dd>
    </div>
  );
}
